import React, { useEffect, useState, useCallback } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./HealthJourney.css";
import "../index.css";
import { getUrlParams } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import { useTranslation } from "react-i18next";

import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai, triggerShare } from "../utils/Utils";

// Shimmer helpers (same pattern as other cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const HealthJourneySkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div className="hj-container">
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />

          {/* Title + subtitle */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "60%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Info icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      <div className="hj-chart-wrapper">
        {/* Chart skeleton */}
        <div
          style={{
            marginTop: 12,
            padding: "0 12px 12px 12px",
          }}
        >
          <div
            style={{
              ...shimmerBaseStyle,
              height: 200,
              borderRadius: 16,
            }}
          />
        </div>

        {/* Buttons row skeleton */}
        <div className="hj-chart-overlay">
          <div className="hj-overlay-bottom row">
            <div className="hj-buttons-inline" style={{ gap: 8 }}>
              {[1, 2, 3].map((i) => (
                <button
                  key={i}
                  type="button"
                  className="hj-btn"
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      ...shimmerBaseStyle,
                      height: 32,
                      minWidth: 90,
                      borderRadius: 999,
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer text skeleton */}
        <div className="hj-footer">
          <div
            style={{
              ...shimmerBaseStyle,
              height: 16,
              width: "70%",
              borderRadius: 8,
              margin: "0 auto",
            }}
          />
        </div>
      </div>
    </div>
  );
};

const FALLBACK_DATA = {
  joinedSince: "Elite for 55 days",
  plots: [
    { from: 0, to: 135, color: "rgba(255, 228, 226)", label: { text: "Sedentary", style: { color: "#4b2a2a" } } },
    { from: 135, to: 336, color: "rgba(255, 245, 204)", label: { text: "Active", style: { color: "#4b4b4b" } } },
    { from: 336, to: 675, color: "rgba(223, 255, 227)", label: { text: "Fit", style: { color: "#4b4b4b" } } },
    { from: 675, to: 1100, color: "rgba(223, 235, 253)", label: { text: "Elite", style: { color: "#4b4b4b" } } },
  ],
  xAxis: ["19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17"],
  yAxis: [787, 786, 780, 788, 814, 852, 886, 890, 872, 870, 881, 878, 909, 912, 922, 902, 876, 869, 861, 835, 788, 800, 810, 761, 772, 776, 777, 792, 796, 768],
  image: "https://goqii-app.s3.amazonaws.com/emailer/36689_1605782957.png",
  text: "My Health Journey",
  healthtext: "Consistency is the key.",
  showInfo: 1,
  infoUrl: "https://goqii.com/app/?fsn=61&fssn=0&fai=%7B%22url%22%3A%22https%3A%2F%2Fgoqii.com%2Flifestylecardinfo%3Fsid%3Dmyhealthjourney%22%7D",
  buttons: [
    { value: 30, id: "btn_30", title: "30 days" },
    { value: 60, id: "btn_60", title: "60 days" },
    { value: 90, id: "btn_90", title: "90 days" }
  ],
  zones: null
};

const DEFAULT_ZONE_COLORS = ["#F76E66", "#F8C01B", "#58C063", "#4B78C1"];

const DARK_PLOT_BAND_COLORS = [
  "rgba(220, 55, 45, 0.28)",
  "rgba(200, 150, 0, 0.28)",
  "rgba(40, 160, 60, 0.28)",
  "rgba(55, 95, 195, 0.28)",
];
const DARK_PLOT_LABEL_COLORS = ["#dd8888", "#ccaa44", "#77cc77", "#7799ee"];

// 60/90 day views would cram 60-90 date labels along the axis, so past
// this many points we label every LABEL_GAP_DAYS-th day instead.
const DENSE_AXIS_THRESHOLD = 30;
const LABEL_GAP_DAYS = 10;

function normalizeData(raw) {
  const obj = raw && typeof raw === "object" ? raw : {};
  const maybeUnwrapped =
    typeof obj.code === "number" && obj.data && typeof obj.data === "object"
      ? obj.data
      : obj;
  const normalized = { ...FALLBACK_DATA, ...maybeUnwrapped };

  normalized.yAxis = Array.isArray(maybeUnwrapped.yAxis)
    ? maybeUnwrapped.yAxis
    : FALLBACK_DATA.yAxis.slice();
  normalized.xAxis = Array.isArray(maybeUnwrapped.xAxis)
    ? maybeUnwrapped.xAxis
    : FALLBACK_DATA.xAxis.slice();
  normalized.plots = Array.isArray(maybeUnwrapped.plots)
    ? maybeUnwrapped.plots
    : FALLBACK_DATA.plots.slice();
  normalized.buttons = Array.isArray(maybeUnwrapped.buttons)
    ? maybeUnwrapped.buttons
    : FALLBACK_DATA.buttons.slice();
  normalized.image = maybeUnwrapped.image || FALLBACK_DATA.image;
  normalized.text = maybeUnwrapped.text || FALLBACK_DATA.text;
  normalized.healthtext = maybeUnwrapped.healthtext || FALLBACK_DATA.healthtext;
  normalized.joinedSince = maybeUnwrapped.joinedSince || FALLBACK_DATA.joinedSince;
  normalized.showInfo =
    typeof maybeUnwrapped.showInfo !== "undefined"
      ? maybeUnwrapped.showInfo
      : FALLBACK_DATA.showInfo;
  normalized.infoUrl = maybeUnwrapped.infoUrl || FALLBACK_DATA.infoUrl;
  normalized.zones = Array.isArray(maybeUnwrapped.zones)
    ? maybeUnwrapped.zones
    : maybeUnwrapped.zones === null
      ? null
      : FALLBACK_DATA.zones;

  return normalized;
}

export default function HealthJourney({ shareConfig } = {}) {
  const { t } = useTranslation();
  const qp = new URL(window.location.href).searchParams;
  const queryGoqiiUserId =
    qp.get("goqiiUserId") ||
    qp.get("goqiiuserid") ||
    qp.get("goqii_id");
  const queryDays = qp.get("days");
  const queryCallingFrom =
    qp.get("callingFrom") ||
    qp.get("callingfrom") ||
    qp.get("calling_from");

  const { encryptedId, theme } = getUrlParams() || {};
  const isDark = theme === "dark" || document.documentElement.classList.contains("dark");
  const initialGoqiiUserId = encryptedId || queryGoqiiUserId || null;
  const initialDays = queryDays ? Number(queryDays) : 30;
  const initialCallingFrom =
    typeof queryCallingFrom === "string" ? queryCallingFrom : undefined;

  const [data, setData] = useState(null);
  const [days, setDays] = useState(initialDays);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const fetchData = useCallback(
    async (uid, daysParam, callingFromParam, showLoading = true) => {
      if (showLoading) setLoading(true);
      setLoadError(null);

      try {
        if (uid) {
          const res = await lifestyleApi.HealthGraphData(
            uid,
            daysParam,
            callingFromParam
          );
          const payload = res?.data?.data || res?.data || res;
          const normalized = normalizeData(payload);
          setData(normalized);
        } else {
          const r = await fetch("/data.json", { cache: "no-store" });
          if (!r.ok) throw new Error(`Fetch /data.json failed: ${r.status}`);
          const j = await r.json();
          const normalized = normalizeData(j?.data || j);
          setData(normalized);
        }
      } catch (err) {
        setLoadError(err.message || String(err));
        try {
          const r2 = await fetch("/data.json", { cache: "no-store" });
          if (r2.ok) {
            const j2 = await r2.json();
            setData(normalizeData(j2?.data || j2));
          } else {
            setData(normalizeData(null));
          }
        } catch (e2) {
          setData(normalizeData(null));
        }
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchData(initialGoqiiUserId, days, initialCallingFrom, true);
  }, []);

  const handleDaysClick = (val) => {
    if (val === days) return;
    setDays(val);
    fetchData(initialGoqiiUserId, val, initialCallingFrom, false);
  };

  if (loading) return <HealthJourneySkeleton />;
  if (!data) return null;

  function makeSeries(daysCount) {
    const baseY = Array.isArray(data.yAxis)
      ? data.yAxis.slice()
      : FALLBACK_DATA.yAxis.slice();
    if (daysCount <= baseY.length)
      return baseY.slice(baseY.length - daysCount);

    const res = [];
    let idx = 0;
    while (res.length < daysCount) {
      const val = baseY[idx % baseY.length];
      const rep = Math.floor(res.length / baseY.length);
      const jitter =
        Math.sin((res.length + 1) * 0.13) * rep * 1.2;
      res.push(Math.round(val + jitter));
      idx++;
    }
    return res;
  }

  function makeXLabels(daysCount) {
    const baseX = Array.isArray(data.xAxis)
      ? data.xAxis.slice()
      : FALLBACK_DATA.xAxis.slice();
    if (daysCount <= baseX.length)
      return baseX.slice(baseX.length - daysCount);

    const res = [];
    let i = 0;
    while (res.length < daysCount) {
      res.push(baseX[i % baseX.length]);
      i++;
    }
    return res;
  }

  function makePlotBands() {
    return (data.plots || FALLBACK_DATA.plots).map((p, i) => ({
      from: p.from,
      to: p.to,
      color: isDark
        ? (DARK_PLOT_BAND_COLORS[i] || "rgba(255,255,255,0.06)")
        : (p.color || "rgba(0,0,0,0.03)"),
      label: {
        text: p.label?.text || null,
        align: "left",
        x: 8,
        verticalAlign: "middle",
        y: 0,
        style: {
          color: isDark
            ? (DARK_PLOT_LABEL_COLORS[i] || "#aaaaaa")
            : (p.label?.style?.color || "#606060"),
          fontSize: "12px",
          fontWeight: "400",
          fontFamily: "Open Sans, sans-serif"
        }
      }
    }));
  }

  function makeZones() {
    if (Array.isArray(data.zones) && data.zones.length > 0) {
      const converted = data.zones.map((z) =>
        typeof z.value === "number"
          ? { value: z.value, color: z.color || DEFAULT_ZONE_COLORS[0] }
          : { color: z.color || DEFAULT_ZONE_COLORS[0] }
      );
      if (!converted.some((z) => typeof z.value !== "number"))
        converted.push({
          color: DEFAULT_ZONE_COLORS.at(-1)
        });
      return converted;
    }
    const plots = (data.plots || FALLBACK_DATA.plots)
      .slice()
      .sort((a, b) => (a.to || 0) - (b.to || 0));
    const zones = plots.map((p, i) => ({
      value: p.to,
      color:
        DEFAULT_ZONE_COLORS[i] ||
        DEFAULT_ZONE_COLORS.at(-1)
    }));
    zones.push({ color: DEFAULT_ZONE_COLORS.at(-1) });
    return zones;
  }

  const seriesData = makeSeries(days);
  const xLabels = makeXLabels(days);
  // Anchored on the latest day so today's date is always labelled:
  // 60 days -> 6 labels, 90 days -> 9 labels.
  const xTickPositions =
    xLabels.length > DENSE_AXIS_THRESHOLD
      ? Array.from(
          { length: Math.floor((xLabels.length - 1) / LABEL_GAP_DAYS) + 1 },
          (_, i) => xLabels.length - 1 - i * LABEL_GAP_DAYS
        ).reverse()
      : undefined;
  const plotBands = makePlotBands();
  const zones = makeZones();

  const chartOptions = {
    chart: {
      type: "spline",
      backgroundColor: "transparent",
      spacing: [0, 0, 0, 0],
      marginTop: 0,
      height: 140,

      events: {
        render: function () {
          const chart = this;

          if (chart.customElements) {
            chart.customElements.forEach((el) => {
              try {
                el.destroy && el.destroy();
              } catch (e) { }
            });
          }
          chart.customElements = [];

          const overlayImg = document.querySelector(".hj-info-icon img");
          if (overlayImg) {
            try {
              const anchor =
                overlayImg.closest("a.hj-info-link");
              const infoUrlFromOptions =
                chart.userOptions &&
                  chart.userOptions._hjData
                  ? chart.userOptions._hjData.infoUrl
                  : null;
              if (anchor && infoUrlFromOptions) {
                anchor.setAttribute("href", infoUrlFromOptions);
                anchor.setAttribute("target", "_blank");
                anchor.setAttribute("rel", "noreferrer");
              }
            } catch (e) { }
            return;
          }
        }
      }
    },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      categories: xLabels,
      tickInterval: 1,
      tickPositions: xTickPositions,
      labels: {
        style: { fontSize: "11px", color: isDark ? "#aaaaaa" : "#4a4a4a", fontFamily: "Open Sans, sans-serif" },
        useHTML: true,
        y: 15,
        formatter: function () {
          return `<span class="hj-xlabel">${this.value}</span>`;
        }
      },
      lineWidth: 0,
      tickLength: 0
    },
    yAxis: {
      min: 0,
      max: 1100,
      title: { text: null },
      gridLineWidth: 0,
      labels: { enabled: false },
      plotBands: plotBands
    },
    tooltip: { enabled: false },
    legend: { style: { fontFamily: "Open Sans, sans-serif" }, enabled: false },
    series: [
      {
        name: "score",
        data: seriesData,
        marker: { enabled: false },
        lineWidth: 3.5,
        zoneAxis: "y",
        zones: zones,
        color:
          zones && zones.length
            ? undefined
            : "#2f66b5"
      }
    ],
    plotOptions: {
      series: {
        states: { hover: { enabled: false } }
      },
      style: { fontFamily: "Open Sans, sans-serif" }
    }
  };

  chartOptions._hjData = data;

  return (
    <div className="hj-container">
      {loadError ? (
        <div className="hj-error-banner" role="status">
          {String(loadError)}
        </div>
      ) : null}

      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/54463_1778846841.png"
        title={data.text}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        subtitle={data.joinedSince}
        showInfo={data.showInfo}
        onInfoClick={() => {
          navigateWithFai(data.text, data.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />

      <div className="hj-chart-wrapper">
        <div className="hj-chart-overlay">
          <div className="hj-overlay-bottom row">
            <div className="hj-buttons-inline">
              {(data.buttons && data.buttons.length > 0 ? data.buttons : [
                    { value: 30, id: "btn_30", titleKey: "healthJourney.buttons.thirtyDays" },
                    { value: 60, id: "btn_60", titleKey: "healthJourney.buttons.sixtyDays" },
                    { value: 90, id: "btn_90", titleKey: "healthJourney.buttons.ninetyDays" },
                  ]).map(
                (b) => (
                  <button
                    key={b.id}
                    id={b.id}
                    onClick={() =>
                      handleDaysClick(b.value)
                    }
                    className={`hj-btn ${b.value === days ? "active" : ""
                      }`}
                  >
                    {b.title || t(b.titleKey)}
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
        />

        <div className="hj-footer">
          {data.healthtext || t("healthJourney.consistencyKey")}
        </div>
      </div>
    </div>
  );
}
